import { makeAutoObservable, runInAction } from 'mobx';
import { handleStateError } from 'src/errors';

import { AuthUser, User } from 'src/models/AuthUser';
import ApiService from 'src/services/ApiService';
import { STORE_NAME, WHS_TOKEN_EXPIRY_KEY, WHS_TOKEN_KEY, WHS_USER_KEY } from 'src/utils/constants';
import { RootStore } from './RootStore';

export default class AuthStore {
  private storeName = STORE_NAME.AUTH_STORE;
  private rootStore: RootStore;
  private apiService: ApiService;

  constructor(rootStore: RootStore, apiService?: ApiService) {
    makeAutoObservable(this);
    this.rootStore = rootStore;
    this.apiService = apiService ?? new ApiService(this.getWhsAccessToken);
  }

  _whsAccessToken: string | null = localStorage.getItem(WHS_TOKEN_KEY);
  _user: User | null = JSON.parse(localStorage.getItem(WHS_USER_KEY) ?? 'null');

  get whsAccessToken(): string | null {
    return this._whsAccessToken ?? localStorage.getItem(WHS_TOKEN_KEY);
  }

  get authorizedUser(): User | null {
    return this._user ?? JSON.parse(localStorage.getItem(WHS_USER_KEY) ?? 'null');
  }

  get isAuthenticated(): boolean {
    return this._whsAccessToken !== null && this._user !== null && !this.isTokenExpired();
  }

  getWhsAccessToken = async (): Promise<string | null | undefined> => {
    return this._whsAccessToken ?? localStorage.getItem(WHS_TOKEN_KEY);
  };

  signIn = async (accessKey: string): Promise<void> => {
    try {
      const authUser = await this.apiService.signInWithKey(accessKey);

      runInAction(() => {
        this._whsAccessToken = authUser.token ?? '';
        this._user = authUser.user ?? null;
      });

      if (authUser.token && authUser.user) {
        this.storeToken(authUser);
      }
    }
    catch (error) {
      handleStateError(error, this.storeName);
    }
  };

  logOut = (): void => {
    runInAction(() => {
      this._whsAccessToken = null;
      this._user = null;
    });

    localStorage.removeItem(WHS_TOKEN_KEY);
    localStorage.removeItem(WHS_TOKEN_EXPIRY_KEY);
    localStorage.removeItem(WHS_USER_KEY);
  };

  private storeToken = (authUser: AuthUser): void => {
    localStorage.setItem(WHS_TOKEN_KEY, authUser.token);
    localStorage.setItem(WHS_TOKEN_EXPIRY_KEY, authUser.expiryTime);
    localStorage.setItem(WHS_USER_KEY, JSON.stringify(authUser.user));
  };

  private isTokenExpired = (): boolean => {
    const token = localStorage.getItem(WHS_TOKEN_KEY) ?? '';
    const expiryTimeStr = localStorage.getItem(WHS_TOKEN_EXPIRY_KEY) ?? '';
    const expiryTime = new Date(expiryTimeStr).getTime();

    return !token || !expiryTimeStr || isNaN(expiryTime) || Date.now() > expiryTime;
  };
}
